import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import StarRating from './StarRating';
import '../styles/ProductReviewsListItem.css';


/** HELPERS */
/**
 * Create and return a display name from customer data.
 * @param {Object} customer An object containing the customer data.
 * @param {Object} customer.User The user data.
 * @param {String} customer.User.firstName The first name of the user.
 * @param {String} customer.User.lastName The last name of the user.
 * @returns {String} The display name for the customer.
 */
const getDisplayNameFromCustomerData = (customer) => {
    if (!customer?.User) {
        return 'Anonymous';
    }
    const { firstName, lastName } = customer.User;
    return `${firstName}${firstName && lastName ? ' ' : ''}${lastName}`;
};

/**
 * Convert a timestamp into a date string.
 * @param {String} timestamp The ISO 8601 timestamp.
 * @returns {String} The formatted version of the timestamp.
 */
const convertToDate = (timestamp) => DateTime
    .fromISO(timestamp, { zone: process.env.APP_TIME_ZONE })
    .toFormat('LLLL d, yyyy');

/**
 * Display a product review.
 * @component
 */
const ProductReviewsListItem = ({ review }) => <div>
    <div
        className={'review-item'}>
        <StarRating
            rating={review.starsRating}
            size={'1.5rem'} />
        <div
            className={'review-item--comment'}>
            {!!review.title && (<>
                <strong>{review.title}</strong>
                <br />
            </>)}
            {review.comment}
        </div>
        <div
            className={'review-item--customer'}>
            {review.author ?? getDisplayNameFromCustomerData(review.Customer)} on {convertToDate(review.createdAt)}
        </div>
    </div>
    {review.ReviewReply && <div
        className={'review-item--reply-container'}>
        <div
            className={'review-item--reply'}>
            <div>
                {review.ReviewReply.comment} {review.ReviewReply.editedAt && <span
                    className={'text-muted text-small fst-italic'}>
                    (Edited)
                </span>}
            </div>
            <div
                className={'review-item--customer'}>
                {review.ReviewReply.Employee.User.fullName ?? 'Admin'} on {convertToDate(review.ReviewReply.updatedAt)}
            </div>
        </div>
    </div>}
</div>;

/** Prop Types */
ProductReviewsListItem.propTypes = {
    /** The review to display. */
    review: PropTypes.shape({
        author: PropTypes.string,
        email: PropTypes.email,
        /** The rating given for the review. */
        starsRating: PropTypes.number.isRequired,
        /** The title provided by the customer/user, if any. */
        title: PropTypes.string.isRequired,
        /** The comment provided by the customer/user, if any. */
        comment: PropTypes.string.isRequired,
        /** The customer data, if available. */
        Customer: PropTypes.shape({
            /** The user data associated to the customer. */
            User: PropTypes.shape({
                /** The first name of the user who provided the review. */
                firstName: PropTypes.string,
                /** The last name of the user who provided the review. */
                lastName: PropTypes.string,
            }),
        }),
        ReviewReply: PropTypes.oneOf([
            null,
            PropTypes.oneOfType([PropTypes.shape({
                /** The comment provided by the business in response to the review. */
                comment: PropTypes.string.isRequired,
                /** The employee who provided the response. */
                Employee: PropTypes.shape({
                    /** The user associated to the employee. */
                    User: PropTypes.shape({
                        /** The full name of the user. */
                        fullName: PropTypes.string.isRequired,
                    }).isRequired,
                }).isRequired,
                /** The date and time the reply was edited. */
                editedAt: PropTypes.string.isRequired,
            })]),
        ]),
        /** The date and time the review was submitted. */
        createdAt: PropTypes.string,
    }),
};

export default ProductReviewsListItem;
